import { EditorView, basicSetup } from "codemirror";
import { keymap } from "@codemirror/view";
import { indentWithTab } from "@codemirror/commands";
import { EditorState, type Extension } from "@codemirror/state";
import { html } from "@codemirror/lang-html";
import { css } from "@codemirror/lang-css";
import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { oneDark } from "@codemirror/theme-one-dark";

export function langFor(path: string): Extension {
  // .vetchka — это JSON-метаинформация паста.
  if (path === ".vetchka" || path.endsWith("/.vetchka")) return json();
  const ext = path.slice(path.lastIndexOf(".") + 1).toLowerCase();
  switch (ext) {
    case "html":
    case "htm":
      return html();
    case "css":
      return css();
    case "js":
    case "mjs":
    case "jsx":
      return javascript({ jsx: ext === "jsx" });
    case "ts":
      return javascript({ typescript: true });
    case "json":
      return json();
    case "md":
      return markdown();
    default:
      return [];
  }
}

export interface EditorHandle {
  view: EditorView;
  getValue: () => string;
  setValue: (v: string) => void;
  destroy: () => void;
}

export interface CreateEditorOptions {
  parent: HTMLElement;
  path: string;
  value: string;
  onChange?: (value: string) => void;
  onSave?: () => void;
}

export function createEditor(opts: CreateEditorOptions): EditorHandle {
  const updateListener = EditorView.updateListener.of((u) => {
    if (u.docChanged) opts.onChange?.(u.state.doc.toString());
  });
  const saveKey = keymap.of([
    {
      key: "Mod-s",
      preventDefault: true,
      run: () => {
        opts.onSave?.();
        return true;
      },
    },
    indentWithTab,
  ]);

  const state = EditorState.create({
    doc: opts.value,
    extensions: [
      basicSetup,
      oneDark,
      langFor(opts.path),
      updateListener,
      saveKey,
      EditorView.theme({
        "&": { height: "100%", fontSize: "12px" },
        ".cm-scroller": { fontFamily: "ui-monospace, Menlo, monospace" },
      }),
    ],
  });
  const view = new EditorView({ state, parent: opts.parent });
  return {
    view,
    getValue: () => view.state.doc.toString(),
    setValue: (v: string) => {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: v } });
    },
    destroy: () => view.destroy(),
  };
}